import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getPendingDealDoc, PENDING_DEAL_EVENT } from '../services/branch';
import { getOnboardingEnabled } from '../services/firebase';
import { Deal } from '../types/deal';
import { useFirebaseImageUrl } from '../hooks/useFirebaseImageUrl';
import { getPageImage, getPageLayout, layoutToStyle } from '../utils/dealImages';
import { localizeDeal } from '../utils/dealLocale';
import { useAwaitingDeal } from '../hooks/useAwaitingDeal';
import CoBrandHeader from './CoBrandHeader';
import CalloutCard from './CalloutCard';
import LanguageSwitcher from './LanguageSwitcher';
import { t } from '../i18n';
import { AppleIcon, GooglePlayIcon } from './appStoreIcons';
import './Auth.css';
import './Promo.css';

const Welcome: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [pendingDeal, setPendingDeal] = useState<Deal | null>(null);
  const productImage = useFirebaseImageUrl(getPageImage(pendingDeal, 'welcome'));
  const [heroLoaded, setHeroLoaded] = useState(false);
  const awaitingDeal = useAwaitingDeal(productImage, heroLoaded);
  const localizedDeal = localizeDeal(pendingDeal);

  useEffect(() => {
    if (currentUser) {
      navigate('/offers');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    const sync = () => {
      const deal = getPendingDealDoc();
      if (deal) setPendingDeal(deal);
    };
    sync();
    window.addEventListener(PENDING_DEAL_EVENT, sync);
    return () => window.removeEventListener(PENDING_DEAL_EVENT, sync);
  }, []);

  const handleGetStarted = () => {
    navigate(getOnboardingEnabled() ? '/onboarding' : '/signup');
  };

  return (
    <div className="hp-page">
      <LanguageSwitcher />
      <div className="hp-split">
        <div className={`hp-hero${awaitingDeal ? ' hp-hero--awaiting' : ''}`}>
          {productImage && (
            <img src={productImage} alt={pendingDeal?.name || ''} className="hp-hero-fill" style={layoutToStyle(getPageLayout(pendingDeal, 'welcome'))} onLoad={() => setHeroLoaded(true)} />
          )}
        </div>

        <div className="hp-panel">
          <div className="hp-panel-inner">
            <CoBrandHeader deal={pendingDeal} />

            <CalloutCard deal={localizedDeal} page="welcome" />

            <div className="hp-heading">
              <h1 className="hp-headline">{localizedDeal?.headline || t('promo.welcomeHeadline')}</h1>
              <p className="hp-subtitle">
                {localizedDeal?.message || t('promo.welcomeSubtitle')}
              </p>
            </div>

            <button className="hp-cta" onClick={handleGetStarted}>
              {localizedDeal?.ctaText || t('promo.getStarted')}
            </button>

            <div className="hp-ratings">
              <div className="hp-rating">
                <div className="hp-rating-meta">
                  <span className="hp-rating-store">
                    App Store <AppleIcon />
                  </span>
                  <span className="hp-stars">★★★★★</span>
                </div>
                <span className="hp-rating-score">4.9</span>
              </div>
              <div className="hp-rating">
                <div className="hp-rating-meta">
                  <span className="hp-rating-store">
                    Google Play <GooglePlayIcon />
                  </span>
                  <span className="hp-stars">★★★★★</span>
                </div>
                <span className="hp-rating-score">4.7</span>
              </div>
            </div>

            <p className="hp-secondary-link">
              {t('promo.alreadyHaveAccount')}{' '}
              <button onClick={() => navigate('/login')}>{t('promo.logIn')}</button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Welcome;
